import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  ImageBackground,
  Image,
  ScrollView
} from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { InstallPrompt } from '../components/InstallPrompt';
// import { InstallPromptDebug } from '../components/InstallPromptDebug';

export default function SimpleLoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const { login, signup, resetPassword, loading, error, clearError } = useAuth();

  const handleSubmit = async () => {
    if (isResetPassword) {
      if (!email) {
        Alert.alert('Error', 'Please enter your email address');
        return;
      }

      try {
        console.log('Attempting to send password reset email to:', email);
        await resetPassword(email);
        console.log('Password reset email sent successfully');
        Alert.alert(
          'Password Reset Email Sent',
          `A password reset email has been sent to ${email}. Please check your inbox and spam folder.`,
          [
            {
              text: 'OK',
              onPress: () => {
                setIsResetPassword(false);
                setPassword('');
              }
            }
          ]
        );
      } catch (err: any) {
        console.error('Password reset error:', err);
        console.error('Error code:', err.code);
        console.error('Error message:', err.message);
        Alert.alert(
          'Error',
          err.code === 'auth/invalid-email' ? 'Invalid email address' :
          err.code === 'auth/user-not-found' ? 'No account found with this email' :
          err.code === 'auth/missing-email' ? 'Please enter an email address' :
          err.code === 'auth/invalid-action-code' ? 'Invalid or expired reset link' :
          `Failed to send reset email: ${err.message || 'Unknown error'}`
        );
      }
      return;
    }

    if (!email || !password) {
      Alert.alert('Error', 'Please fill in all fields');
      return;
    }

    try {
      if (isSignup) {
        await signup(email, password);
        Alert.alert('Success', 'Account created successfully!');
      } else {
        await login(email, password);
      }
    } catch (err: any) {
      Alert.alert(
        'Error',
        err.code === 'auth/invalid-email' ? 'Invalid email address' :
        err.code === 'auth/user-not-found' ? 'User not found' :
        err.code === 'auth/wrong-password' ? 'Incorrect password' :
        err.code === 'auth/email-already-in-use' ? 'Email already in use' :
        err.code === 'auth/weak-password' ? 'Password too weak' :
        err.message || 'Authentication failed'
      );
    }
  };

  return (
    <ImageBackground
      source={require('../../assets/ocean-image.png')}
      style={styles.backgroundImage}
      resizeMode="cover"
    >
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <InstallPrompt />

        <View style={styles.innerContainer}>
        <View style={styles.logoContainer}>
          <Image 
            source={require('../../assets/OceanInsightLogo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={styles.title}>Ocean Insight Pro</Text>
        </View>
        <Text style={styles.subtitle}>
          {isResetPassword ? 'Reset Password' : isSignup ? 'Create Account' : 'Sign In'}
        </Text>

        <TextInput
          style={styles.input}
          placeholder="Email"
          value={email}
          onChangeText={setEmail}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
        />

        {!isResetPassword && (
          <TextInput
            style={styles.input}
            placeholder="Password"
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            autoCapitalize="none"
          />
        )}

        <TouchableOpacity 
          style={[styles.button, loading && styles.buttonDisabled]}
          onPress={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator color="white" />
          ) : (
            <Text style={styles.buttonText}>
              {isResetPassword ? 'Send Reset Email' : isSignup ? 'Sign Up' : 'Sign In'}
            </Text>
          )}
        </TouchableOpacity>

        {!isResetPassword && !isSignup && (
          <TouchableOpacity
            style={styles.forgotPasswordButton}
            onPress={() => {
              setIsResetPassword(true);
              setPassword('');
              clearError();
            }}
          >
            <Text style={styles.forgotPasswordText}>Forgot Password?</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={styles.switchButton}
          onPress={() => {
            if (isResetPassword) {
              setIsResetPassword(false);
              setPassword('');
            } else {
              setIsSignup(!isSignup);
            }
            clearError();
          }}
        >
          <Text style={styles.switchText}>
            {isResetPassword
              ? 'Back to Sign In'
              : isSignup
              ? 'Already have an account? Sign In'
              : "Don't have an account? Sign Up"}
          </Text>
        </TouchableOpacity>
      </View>
        </ScrollView>
    </KeyboardAvoidingView>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  backgroundImage: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  container: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)', // Semi-transparent overlay
  },
  scrollContent: {
    flexGrow: 1,
  },
  innerContainer: {
    flex: 1,
    justifyContent: 'center',
    padding: 20,
    paddingHorizontal: 30,
    minHeight: 600,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  logo: {
    width: 100,
    height: 100,
    marginBottom: 20,
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: 5,
    textShadowColor: 'rgba(0, 0, 0, 0.8)',
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 5,
  },
  subtitle: {
    fontSize: 22,
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: 40,
    textShadowColor: 'rgba(0, 0, 0, 0.8)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 12,
    padding: 18,
    marginBottom: 16,
    fontSize: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 5,
  },
  button: {
    backgroundColor: '#0066cc',
    borderRadius: 12,
    padding: 18,
    alignItems: 'center',
    marginTop: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 5,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  buttonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: '600',
  },
  switchButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  switchText: {
    color: '#FFFFFF',
    fontSize: 16,
  },
  forgotPasswordButton: {
    marginTop: 15,
    alignItems: 'center',
  },
  forgotPasswordText: {
    color: '#FFFFFF',
    fontSize: 15,
    textDecorationLine: 'underline',
  },
});